import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { LoginLayout } from '../components/auth/LoginLayout';
import QubeProLogoSrc from '../assets/QubeProLogo.svg';
import { Icon } from '../components/icons/Icon';
import { SUPPORTED_LANGUAGES, NATIVE_LANGUAGE_NAMES, normalizeLanguage, type SupportedLanguage } from '../utils/language.utils';
import { LoginForm } from '../components/auth/LoginForm';
import { useProductBranding } from '../hooks/useProductBranding';
import { getApplicationCodeForApi } from '../config/app.config';

export interface LoginPageProps {
  apiConfig?: unknown;
  onLogin?: (authData: any) => void;
  onError?: (error: any) => void;
  redirectPath?: string;
}

function Login({ apiConfig: _apiConfig, onLogin, onError, redirectPath }: LoginPageProps = {}) {
  const { isAuthenticated, user, language, updateLanguage, databaseInitializing, databaseInitializationMessage, databaseError } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const t = useTranslation(language);
  const navigate = useNavigate();
  const loginBrandFallback = t.common.productName ?? getApplicationCodeForApi();
  const { productTitle: loginProductTitle } = useProductBranding(loginBrandFallback);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (databaseInitializing || databaseError) return undefined;
      const targetPath = redirectPath || '/';
      navigate(targetPath, { replace: true });
    }
    return undefined;
  }, [isAuthenticated, user, databaseInitializing, databaseError, navigate, redirectPath]);

  useEffect(() => {
    if (!isAuthenticated) {
      const savedLanguage = localStorage.getItem('qubepro_language');
      if (savedLanguage && savedLanguage !== language) {
        handleLanguageChange(normalizeLanguage(savedLanguage));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const [showInitializingScreen, setShowInitializingScreen] = useState(false);

  const handleLanguageChange = useCallback(async (nextLang: SupportedLanguage) => {
    try {
      await updateLanguage(nextLang);
    } catch (err) {
      if (import.meta.env.MODE === 'development') {
        console.error('Failed to update language:', err);
      }
    }
  }, [updateLanguage]);

  return (
    <LoginLayout language={language} feature={<><div className="feature-surface">
            <div className="feature-header">
              <div className="feature-badge">
                <span className="badge-text">{loginProductTitle}</span>
              </div>
              <h1 className="feature-title">
                {t.login.featureTitle}
              </h1>
              <p className="feature-description">
                {t.login.featureDescription}
              </p>
            </div>

            <div className="feature-cards">
              <div className="feature-card">
                <div className="feature-card-icon">
                  <Icon name="companies" size={48} />
                </div>
                <h3 className="feature-card-title">
                  {t.login.featureCard1Title}
                </h3>
                <p className="feature-card-description">
                  {t.login.featureCard1Description}
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-card-icon">
                  <Icon name="users" size={48} />
                </div>
                <h3 className="feature-card-title">
                  {t.login.featureCard2Title}
                </h3>
                <p className="feature-card-description">
                  {t.login.featureCard2Description}
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-card-icon">
                  <Icon name="dashboard" size={48} />
                </div>
                <h3 className="feature-card-title">
                  {t.login.featureCard3Title}
                </h3>
                <p className="feature-card-description">
                  {t.login.featureCard3Description}
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-card-icon">
                  <Icon name="settings" size={48} />
                </div>
                <h3 className="feature-card-title">
                  {t.login.featureCard4Title}
                </h3>
                <p className="feature-card-description">
                  {t.login.featureCard4Description}
                </p>
              </div>
            </div>
          </div></>}>

        <div className="login-form-panel">
          <div className="login-controls-top">
            <button
              type="button"
              className="theme-toggle-button"
              onClick={toggleTheme}
              title={theme === 'light' ? t.login.darkMode : t.login.lightMode}
              aria-label={theme === 'light' ? t.login.darkMode : t.login.lightMode}
            >
              {theme === 'light' ? <Icon name="darkMode" size={18} /> : <Icon name="lightMode" size={18} />}
            </button>
          </div>

          <div className="login-form-content">
            {databaseInitializing || showInitializingScreen ? (
              <div className="database-initialization-screen">
                <div className="spinner" />
                <h2>{t.login.pleaseWait}</h2>
                <p>{databaseInitializationMessage || t.login.serverConfiguring}</p>
                <p>{t.login.dbCreating}</p>
              </div>
            ) : (
              <>
                <div className="login-header">
                  <div className="login-logo-container">
                    <img src={QubeProLogoSrc} className="login-logo-icon" alt="" aria-hidden="true" />
                  </div>
                  <div className="login-logo-text">{loginProductTitle}</div>
                  <h1>{t.login.loginPageTitle}</h1>
                </div>

                <LoginForm
                  onShowInitializing={setShowInitializingScreen}
                  onError={onError}
                  onSuccess={() => { if (onLogin) onLogin({}); }}
                />

                <div className="login-controls-bottom">
                  <div className="language-switcher" aria-label={t.login.languageToggleLabel}>
                    {SUPPORTED_LANGUAGES.map((langCode) => {
                      const label = NATIVE_LANGUAGE_NAMES[langCode] ?? langCode;
                      return (
                        <button
                          key={langCode}
                          type="button"
                          className={`language-pill ${language === langCode ? 'active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLanguageChange(langCode);
                          }}
                          aria-label={label}
                          aria-pressed={language === langCode}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
    </LoginLayout>
  );
}

export default Login;
